'use client';

import { useState, useEffect, useRef } from 'react';
import AppImage from '@/components/ui/AppImage';
import type { Testimonial } from '@/lib/cms';

interface TestimonialsSectionProps {
  testimonials: Testimonial[];
}

export default function TestimonialsSection({ testimonials }: TestimonialsSectionProps) {
  const [activeIdx, setActiveIdx] = useState(0);
  const sectionRef = useRef<HTMLDivElement>(null);

  const prev = () =>
    setActiveIdx((i) => (i - 1 + (testimonials?.length || 1)) % (testimonials?.length || 1));
  const next = () => setActiveIdx((i) => (i + 1) % (testimonials?.length || 1));

  useEffect(() => {
    const items = sectionRef?.current?.querySelectorAll('.reveal');
    if (!items) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.remove('hidden-start');
        });
      },
      { threshold: 0.1 }
    );
    items?.forEach((el) => observer?.observe(el));
    return () => observer?.disconnect();
  }, []);

  // Auto-advance
  useEffect(() => {
    const timer = setInterval(next, 6000);
    return () => clearInterval(timer);
  }, [testimonials?.length]);

  const active = testimonials?.[activeIdx];

  if (!testimonials?.length) return null;

  return (
    <section id="reviews" className="py-24 bg-primary overflow-hidden relative" ref={sectionRef}>
      {/* Background texture */}
      <div className="absolute inset-0 opacity-5">
        <div
          className="absolute top-0 left-0 w-full h-full"
          style={{
            backgroundImage:
              'radial-gradient(circle at 20% 50%, #C9956A 0%, transparent 50%), radial-gradient(circle at 80% 50%, #8B4B6B 0%, transparent 50%)',
          }}
        />
      </div>
      <div className="max-w-7xl mx-auto px-6 relative z-10">
        {/* Header */}
        <div className="mb-14 reveal hidden-start">
          <span className="text-xs font-mono text-accent/60 uppercase tracking-widest block mb-3 border-b border-white/10 w-fit pb-1">
            04 — Client Stories
          </span>
          <h2 className="font-display text-4xl md:text-6xl font-medium text-white tracking-tight leading-tight">
            What clients
            <br />
            <span className="italic text-accent">are saying</span>
          </h2>
        </div>

        {/* Testimonial Slider */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-start reveal hidden-start reveal-delay-1">
          {/* Photo */}
          <div className="md:col-span-4">
            <div className="relative aspect-[4/5] rounded-4xl overflow-hidden border border-white/10">
              {testimonials?.map((t, i) => (
                <div
                  key={t.id}
                  className={`testimonial-slide absolute inset-0 ${i === activeIdx ? 'opacity-100 z-10' : 'opacity-0 z-0'}`}
                >
                  <AppImage
                    src={t?.nail_photo}
                    alt={`Nail art created for client ${t?.name} — ${t?.service} service`}
                    fill
                    className="object-cover"
                    quality={80}
                  />

                  <div className="absolute bottom-4 right-4 bg-primary/80 backdrop-blur-sm rounded-xl px-3 py-1.5">
                    <p className="text-[10px] font-mono text-white/60 uppercase tracking-widest">
                      {t?.service}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Content */}
          <div className="md:col-span-8 flex flex-col justify-between h-full">
            <div className="relative min-h-[320px]">
              {testimonials?.map((t, i) => (
                <div
                  key={t.id}
                  className={`testimonial-slide absolute top-0 left-0 w-full ${i === activeIdx ? 'opacity-100 z-10' : 'opacity-0 z-0 pointer-events-none'}`}
                >
                  {/* Stars */}
                  <div className="flex gap-1 mb-6">
                    {Array.from({ length: t?.rating })?.map((_, j) => (
                      <svg key={j} className="w-4 h-4 fill-accent text-accent" viewBox="0 0 24 24">
                        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                      </svg>
                    ))}
                  </div>

                  {/* Quote mark */}
                  <svg
                    className="w-8 h-8 text-white/10 mb-4"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                  >
                    <path d="M14.017 21L14.017 18C14.017 16.895 14.912 16 16.017 16H19.017C19.569 16 20.017 15.552 20.017 15V9C20.017 8.448 19.569 8 19.017 8H15.017C14.465 8 14.017 8.448 14.017 9V11C14.017 11.552 13.569 12 13.017 12H12.017V5H22.017V15C22.017 18.314 19.331 21 16.017 21H14.017ZM5.017 21L5.017 18C5.017 16.895 5.912 16 7.017 16H10.017C10.569 16 11.017 15.552 11.017 15V9C11.017 8.448 10.569 8 10.017 8H6.017C5.465 8 5.017 8.448 5.017 9V11C5.017 11.552 4.569 12 4.017 12H3.017V5H13.017V15C13.017 18.314 10.331 21 7.017 21H5.017Z" />
                  </svg>

                  <blockquote className="font-display text-xl md:text-2xl font-medium text-white leading-relaxed tracking-tight mb-8 max-w-xl">
                    <span className="italic text-white/70 block mb-2">
                      "{t?.quote?.split('.')?.[0]}."
                    </span>
                    {t?.quote?.substring(t?.quote?.indexOf('.') + 2)}
                  </blockquote>

                  {/* Client info */}
                  <div className="flex items-center gap-4">
                    <div className="w-11 h-11 rounded-full overflow-hidden border-2 border-accent/30 flex-shrink-0">
                      <AppImage
                        src={t?.photo}
                        alt={`Portrait of ${t?.name}, satisfied nail art client`}
                        width={44}
                        height={44}
                        className="object-cover w-full h-full"
                      />
                    </div>
                    <div>
                      <p className="text-white font-semibold text-sm">{t?.name}</p>
                      <p className="text-white/50 text-xs font-mono uppercase tracking-wide">
                        {t?.location}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Controls */}
            <div className="flex items-center gap-4 mt-8">
              <button
                onClick={prev}
                className="w-10 h-10 border border-white/20 rounded-full flex items-center justify-center text-white/60 hover:text-white hover:border-white/40 transition-all duration-200"
                aria-label="Previous testimonial"
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="m15 18-6-6 6-6" />
                </svg>
              </button>
              <div className="flex gap-2">
                {testimonials?.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setActiveIdx(i)}
                    className={`h-1.5 rounded-full transition-all duration-400 ${i === activeIdx ? 'w-6 bg-accent' : 'w-1.5 bg-white/20'}`}
                    aria-label={`Go to testimonial ${i + 1}`}
                  />
                ))}
              </div>
              <button
                onClick={next}
                className="w-10 h-10 border border-white/20 rounded-full flex items-center justify-center text-white/60 hover:text-white hover:border-white/40 transition-all duration-200"
                aria-label="Next testimonial"
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="m9 18 6-6-6-6" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
